// src/components/scenario/ScenarioExperience.js - FIXED VERSION
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Shield, Clock, X, CheckCircle } from 'lucide-react';

// --- Import Data and Simulators ---
import { sampleEmails } from '../../data/phishingEmails';
import { useAIAssessment } from '../ai/AIAssessmentProvider';
import PhishingEmailSimulator from '../simulation/PhishingEmailSimulator';
import WebBrowserSecuritySimulator from '../simulation/WebBrowserSecuritySimulator';
import FileSystemSecuritySimulator from '../simulation/FileSystemSecuritySimulator';
import NetworkSecuritySimulator from '../simulation/NetworkSecuritySimulator';
import SimulatedOS from '../simulation/SimulatedOS';
import { getChallengeById, submitGameResults } from '../../api/games.api';

// --- Mock Data Functions (can be replaced by API calls) ---
const getWebScenarios = () => [
  { id: 'web-1', initialUrl: 'https://www.paypal-logon-sv.com/signin', threatType: 'phishing', isThreat: true, description: 'You have received a link to your PayPal account. Does this page look legitimate?' }
];
const getFileSystemScenarios = () => [
  { id: 'file-1', fileSystem: [{ name: 'Urgent_Invoice.exe', path: '/downloads/', type: 'file', size: '1.2MB' }], threatType: 'malware', description: 'An invoice was just downloaded. Analyze the file system for threats.' }
];
const getNetworkScenarios = () => [
  { id: 'net-1', networkTraffic: [{ sourceIp: '192.168.1.100', destIp: '104.18.32.227', protocol: 'HTTPS', info: 'Suspicious outbound connection to known C2 server.' }], threatType: 'intrusion', description: 'The network monitor has flagged suspicious traffic. Investigate the connection.' }
];
const getOSScenarios = () => [
  { id: 'os-1', osType: 'windows', threatType: 'permission-escalation', description: 'A script is requesting elevated permissions. Should you allow it?' }
];

const ScenarioExperience = ({ apiService, userProfile, gamificationEngine }) => {
  const { challengeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getPersonalizedContent } = useAIAssessment();

  // --- Core State ---
  const [challengeInfo, setChallengeInfo] = useState(null);
  const [simulationType, setSimulationType] = useState('');
  const [scenarios, setScenarios] = useState([]);
  const [simulationCompleted, setSimulationCompleted] = useState(false);
  const [results, setResults] = useState(null);
  const [submittingResults, setSubmittingResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startTime, setStartTime] = useState(Date.now());
  const [autoProgression, setAutoProgression] = useState(true);
  const [countdown, setCountdown] = useState(0);
  const [nextChallengeTimer, setNextChallengeTimer] = useState(null);

  // --- Routing context (arena/topic) for proper sequencing ---
  const arenaId = searchParams.get('arena');
  const topicFilter = searchParams.get('topic');

  // --- Initialization Effect ---
  useEffect(() => {
    const initializeScenario = async () => {
      try {
        setLoading(true);
        setError(null);

        // console.log('🎯 Initializing scenario for challenge:', challengeId);

        // Try to load challenge from backend
        let challenge;
        try {
          const response = await getChallengeById(challengeId);
          if (response.success && response.data) {
            challenge = response.data;
            // console.log('✅ Challenge loaded from backend:', challenge.title);
          } else {
            throw new Error('Invalid challenge response');
          }
        } catch (apiError) {
          console.warn('⚠️ Backend failed, creating fallback challenge:', apiError.message);
          challenge = createFallbackChallenge(challengeId);
        }

        setChallengeInfo(challenge);

        // Determine simulation type
        const type = determineSimulationType(challenge, challengeId);
        setSimulationType(type);

        // Load appropriate scenarios
        const scenarioData = await loadScenariosForType(type, challenge);
        setScenarios(scenarioData);

        // console.log('🎮 Scenario initialized:', { type, scenarios: scenarioData.length });

      } catch (error) {
        console.error('❌ Failed to initialize scenario:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    initializeScenario();

    // Cleanup timer on unmount
    return () => {
      if (nextChallengeTimer) {
        clearTimeout(nextChallengeTimer);
      }
    };
  }, [challengeId]);

  const createFallbackChallenge = (challengeId) => {
    const fallbackData = {
      'phishing': {
        id: challengeId,
        title: 'Phishing Email Detection',
        description: 'Identify phishing attempts in your inbox',
        category: 'phishing',
        difficulty: 'medium',
        xpReward: 100,
        usingFallback: true
      },
      'web-security': {
        id: challengeId,
        title: 'Web Security Challenge',
        description: 'Identify malicious websites and downloads',
        category: 'web_security',
        difficulty: 'medium',
        xpReward: 120,
        usingFallback: true
      },
      'network-security': {
        id: challengeId,
        title: 'Network Security Analysis',
        description: 'Analyze network traffic for threats',
        category: 'network_security',
        difficulty: 'hard',
        xpReward: 150,
        usingFallback: true
      }
    };

    return fallbackData[challengeId] || fallbackData['phishing'];
  };

  const determineSimulationType = (challenge, challengeId) => {
    // Check explicit type first
    if (challenge?.type === 'simulation') {
      if (challengeId.includes('phishing') || challenge.category?.includes('phishing')) {
        return 'phishing';
      }
      if (challengeId.includes('web') || challenge.category?.includes('web')) {
        return 'web-security';
      }
      if (challengeId.includes('network') || challenge.category?.includes('network')) {
        return 'network-security';
      }
      if (challengeId.includes('file') || challenge.category?.includes('file')) {
        return 'file-system-security';
      }
    }

    // Fallback based on challengeId
    if (challengeId.includes('phishing') || challengeId.includes('email')) return 'phishing';
    if (challengeId.includes('web') || challengeId.includes('browser')) return 'web-security';
    if (challengeId.includes('network')) return 'network-security';
    if (challengeId.includes('file') || challengeId.includes('system')) return 'file-system-security';
    if (challengeId.includes('os') || challengeId.includes('password')) return 'os';

    return 'phishing'; // Default fallback
  };

  const loadScenariosForType = async (type, challenge) => {
    try {
      // If challenge has content, use it
      if (challenge?.content && Array.isArray(challenge.content) && challenge.content.length > 0) {
        // console.log('📝 Using challenge content:', challenge.content.length, 'scenarios');
        return challenge.content;
      }

      // Try to get AI-personalized content
      if (getPersonalizedContent && userProfile) {
        try {
          const personalizedScenarios = await getPersonalizedContent(type, userProfile);
          if (personalizedScenarios && personalizedScenarios.length > 0) {
            // console.log('🤖 Using AI-personalized scenarios:', personalizedScenarios.length);
            return personalizedScenarios;
          }
        } catch (aiError) {
          console.warn('⚠️ AI personalization failed:', aiError.message);
        }
      }

      // Use appropriate fallback scenarios
      switch (type) {
        case 'phishing':
          console.log('📧 Using sample phishing emails');
          return sampleEmails.slice(0, 5); // Limit to 5 emails for better experience
        case 'web-security':
          console.log('🌐 Using web security scenarios');
          return getWebScenarios();
        case 'file-system-security':
          console.log('📁 Using file system scenarios');
          return getFileSystemScenarios();
        case 'network-security':
          console.log('🌐 Using network security scenarios');
          return getNetworkScenarios();
        case 'os':
        case 'password':
          console.log('💻 Using OS scenarios');
          return getOSScenarios();
        default:
          console.log('📧 Using default phishing scenarios');
          return sampleEmails.slice(0, 3);
      }
    } catch (error) {
      console.error('❌ Error loading scenarios:', error);
      return sampleEmails.slice(0, 3); // Ultimate fallback
    }
  };

  const handleSimulationComplete = async (summary) => {
    console.log('🎯 Simulation completed with summary:', summary);

    const endTime = Date.now();
    const durationSeconds = Math.round((endTime - startTime) / 1000);
    const score = summary.total > 0 ? Math.round((summary.correct / summary.total) * 100) : 0;

    // **FIXED**: Only calculate XP if user got correct answers
    let earnedXp = 0;
    if (score > 0) {
      const baseXp = challengeInfo?.points || challengeInfo?.xpReward || 50;
      const difficultyMultiplier = {
        'beginner': 1.0,
        'intermediate': 1.2,
        'advanced': 1.5
      }[challengeInfo?.difficulty] || 1.0;

      earnedXp = Math.round(baseXp * (score / 100) * difficultyMultiplier);
    }

    const finalResults = {
      ...summary,
      completionTime: durationSeconds,
      score,
      earnedXp
    };

    setResults(finalResults);
    setSimulationCompleted(true);

    console.log('💾 Starting result submission process...');

    // Submit results to backend (don't wait for completion)
    submitResultsToBackend(finalResults, summary);

    // Clear the stored challenge to prevent reuse
    sessionStorage.removeItem('currentChallenge');

    // Award XP via gamification engine (local)
    if (gamificationEngine && gamificationEngine.recordActivityComplete) {
      try {
        gamificationEngine.recordActivityComplete(challengeId, {
          score,
          xpValue: earnedXp
        });
        console.log('🎮 Gamification engine updated successfully');
      } catch (error) {
        console.warn('⚠️ Error recording activity completion in gamification engine:', error);
      }
    }

    // Auto-progress to next challenge if enabled and passing score
    if (autoProgression && score >= 70) {
      console.log('🔄 Auto-progression enabled, starting countdown...');
      startAutoProgressionCountdown();
    }
  };

  const startAutoProgressionCountdown = () => {
    setCountdown(8); // 8 second countdown

    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          loadNextChallenge();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Store interval for cleanup
    setNextChallengeTimer(countdownInterval);
  };

  const loadNextChallenge = async () => {
    // Helper function to get arena ID from challenge or query params
    const getArenaId = () => {
      // Try to get arena from query params first
      if (arenaId) return arenaId;

      // Fallback to challenge's category/arena
      const arenaFromChallenge =
        challengeInfo?.associated_arena ||
        challengeInfo?.metadata?.arena ||
        challengeInfo?.arena ||
        challengeInfo?.category; // Use category as fallback (e.g., 'phishing_social_engineering')

      return arenaFromChallenge;
    };

    const resolvedArenaId = getArenaId();

    // Helper function to build challenges URL with arena
    const buildChallengesUrl = () => {
      if (resolvedArenaId) {
        return `/challenges?arena=${resolvedArenaId}${topicFilter ? `&topic=${encodeURIComponent(topicFilter)}` : ''}`;
      }
      return '/dashboard';
    };

    try {
      console.log('🔄 Loading next challenge with sequential selection...');

      if (!apiService) {
        console.warn('⚠️ apiService not available, returning to dashboard');
        navigate(buildChallengesUrl());
        return;
      }

      // Build query similar to ChallengeHub so order matches arena view
      // Use perDifficulty to fetch 10 challenges per difficulty level
      const apiParams = {
        limit: '10',
        perDifficulty: 'true'
      };
      if (resolvedArenaId) apiParams.arena = resolvedArenaId;

      const topics = (topicFilter || '').trim();
      if (topics) apiParams.topics = topics;

      const response = await apiService.get(
        `/challenges?${new URLSearchParams(apiParams).toString()}`
      );

      if (response.success && Array.isArray(response.data) && response.data.length > 0) {
        // Sort by explicit order (or id) exactly like ChallengeHub
        const sorted = [...response.data].sort((a, b) => {
          return (a.order || a.id) - (b.order || b.id);
        });

        // Prefer staying within the same difficulty level
        const currentDifficulty =
          challengeInfo?.difficulty != null ? String(challengeInfo.difficulty) : null;

        const sameDifficultyList = currentDifficulty
          ? sorted.filter((c) => String(c.difficulty) === currentDifficulty)
          : sorted;

        const listToUse = sameDifficultyList.length > 0 ? sameDifficultyList : sorted;

        // Find current challenge index in that ordered list
        const currentIndex = listToUse.findIndex(
          (c) => String(c.id) === String(challengeId)
        );

        if (currentIndex !== -1) {
          const nextIndex = currentIndex + 1;

          if (nextIndex < listToUse.length) {
            const nextChallenge = listToUse[nextIndex];

            const arenaParams = resolvedArenaId
              ? `?arena=${resolvedArenaId}${topicFilter ? `&topic=${encodeURIComponent(topicFilter)}` : ''
              }`
              : '';

            navigate(`/scenario/${nextChallenge.id}${arenaParams}`);
            return;
          }

          console.log('ℹ️ Reached end of challenge sequence for this difficulty/arena');
          navigate(buildChallengesUrl());
          return;
        }
      }

      // Fallback to arena view or dashboard if no next challenge found
      console.log('ℹ️ No next challenge found, returning to arena/dashboard');
      navigate(buildChallengesUrl());
    } catch (error) {
      console.error('❌ Error loading next challenge:', error);
      navigate(buildChallengesUrl());
    }
  };

  const cancelAutoProgression = () => {
    if (nextChallengeTimer) {
      clearInterval(nextChallengeTimer);
      setNextChallengeTimer(null);
    }
    setCountdown(0);
    setAutoProgression(false);
  };

  const submitResultsToBackend = async (finalResults, summary) => {
    if (submittingResults || !challengeInfo?.id) {
      console.log('⚠️ Results already being submitted or missing challenge ID, skipping...');
      return;
    }

    setSubmittingResults(true);

    try {
      const gameData = {
        challengeId: challengeInfo.id,
        gameType: simulationType,
        score: finalResults.score,
        timeSpent: finalResults.completionTime,
        decisions: summary.decisions || {},
        correct: finalResults.correct || summary.correct || 0,
        incorrect: finalResults.incorrect || summary.incorrect || 0,
        total: finalResults.total || summary.total || 0,
        earnedXp: finalResults.earnedXp,
        completionData: {
          simulator_type: simulationType,
          challenge_category: challengeInfo.category,
          difficulty: challengeInfo.difficulty,
          scenarios_completed: finalResults.total || 0,
          accuracy_rate: finalResults.score,
          completion_timestamp: new Date().toISOString(),
          using_fallback: challengeInfo.usingFallback || false,
          auto_progression_enabled: autoProgression
        }
      };

      // console.log('🎮 Submitting scenario results to backend:', gameData);

      const response = await submitGameResults(gameData);

      // if (response.success) {
      //   console.log('✅ Scenario results saved to database:', response.data);
      // } else {
      //   console.warn('⚠️ Backend returned error for scenario results:', response.message);
      // }

    } catch (error) {
      console.error('❌ Failed to submit scenario results to backend:', error);
      // Don't show error to user as the scenario still completed successfully
    } finally {
      setSubmittingResults(false);
    }
  };

  // --- Dynamic Renderer for Simulators ---
  const renderSimulator = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (!scenarios || scenarios.length === 0) {
      return (
        <div className="p-8 text-center text-red-400">
          No scenarios available for this challenge.
        </div>
      );
    }

    // Render the correct simulator component based on the determined type
    switch (simulationType) {
      case 'phishing':
        return <PhishingEmailSimulator emails={scenarios} onComplete={handleSimulationComplete} difficulty={challengeInfo?.difficulty || 'medium'} />;
      case 'web-security':
        return <WebBrowserSecuritySimulator scenarios={scenarios} onComplete={handleSimulationComplete} />;
      case 'file-system-security':
        return <FileSystemSecuritySimulator scenarios={scenarios} onComplete={handleSimulationComplete} />;
      case 'network-security':
        return <NetworkSecuritySimulator scenarios={scenarios} onComplete={handleSimulationComplete} />;
      case 'os':
      case 'password':
        return <SimulatedOS osType="windows" onComplete={handleSimulationComplete} />;
      default:
        return <div className="p-8 text-center text-red-400">Error: Could not load simulation for type '{simulationType}'.</div>;
    }
  };

  // --- Completion Screen ---
  const renderCompletionScreen = () => (
    <div className="bg-gray-800 rounded-lg p-8 text-center max-w-2xl mx-auto">
      <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
      <h2 className="text-3xl font-bold mb-2">Challenge Complete!</h2>
      <p className="text-gray-400 mb-2">
        You've successfully completed the '{challengeInfo?.title}' challenge.
      </p>

      {submittingResults && (
        <p className="text-sm text-blue-400 mb-4">
          💾 Saving results to database...
        </p>
      )}

      {challengeInfo?.usingFallback && (
        <p className="text-sm text-yellow-400 mb-4">
          ⚠️ This scenario used fallback content due to server issues.
        </p>
      )}

      {/* Auto-progression countdown */}
      {autoProgression && countdown > 0 && results?.score >= 70 && (
        <div className="bg-blue-900/30 border border-blue-500 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-300 font-medium">Next challenge starting in {countdown} seconds...</p>
              <p className="text-sm text-blue-400">Great job! Moving to the next challenge automatically.</p>
            </div>
            <button
              onClick={cancelAutoProgression}
              className="text-gray-400 hover:text-white"
              title="Cancel auto-progression"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 text-white">
        <div className="bg-gray-700 p-4 rounded-lg">
          <div className="text-sm text-gray-400">Final Score</div>
          <div className="text-2xl font-bold">{results?.score || 0}%</div>
        </div>
        <div className="bg-gray-700 p-4 rounded-lg">
          <div className="text-sm text-gray-400">XP Earned</div>
          <div className="text-2xl font-bold text-purple-400">+{results?.earnedXp || 0} XP</div>
        </div>
        <div className="bg-gray-700 p-4 rounded-lg">
          <div className="text-sm text-gray-400">Time Taken</div>
          <div className="text-2xl font-bold">{results?.completionTime || 0}s</div>
        </div>
      </div>

      <div className="space-y-3">
        <button
          onClick={loadNextChallenge}
          className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium flex items-center justify-center space-x-2"
        >
          <span>Next Challenge</span>
          <Shield size={16} />
        </button>

        <button
          onClick={() => navigate('/dashboard')}
          className="w-full px-6 py-3 bg-gray-600 hover:bg-gray-500 rounded-lg font-medium"
        >
          Return to Dashboard
        </button>

        <button
          onClick={() => setAutoProgression(!autoProgression)}
          className={`w-full px-6 py-3 rounded-lg font-medium ${autoProgression
            ? 'bg-green-600 hover:bg-green-500'
            : 'bg-gray-600 hover:bg-gray-500'
            }`}
        >
          Auto-progression: {autoProgression ? 'ON' : 'OFF'}
        </button>
      </div>
    </div>
  );

  // --- Error Screen ---
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <Shield size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">Error Loading Scenario</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // --- Main Render ---
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700"
            >
              ←
            </button>
            <div>
              <h1 className="text-2xl font-bold flex items-center">
                <Shield className="mr-3 text-blue-500" size={24} />
                {challengeInfo?.title || 'Security Scenario'}
              </h1>
              <p className="text-gray-400 text-sm">
                {challengeInfo?.description || 'Interactive cybersecurity training'}
              </p>
            </div>
          </div>

          {!simulationCompleted && (
            <div className="flex items-center space-x-2 text-gray-400">
              <Clock size={16} />
              <span>{Math.floor((Date.now() - startTime) / 1000)}s</span>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto">
          {simulationCompleted ? renderCompletionScreen() : renderSimulator()}
        </div>
      </div>
    </div>
  );
};

export default ScenarioExperience;